/**
 * Asistente virtual — botón flotante + panel lateral deslizable.
 * UX inspirada en ChatGPT / Intercom AI.
 *
 * - Historial en memoria local + persistencia server-side
 * - Markdown render con links internos clickeables
 * - Quick actions predefinidas
 * - Estado "pensando..." mientras llega la respuesta
 */
import { useState } from 'react';
import { Sparkles, X, Send, Loader2, FileText, Hash, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Markdown } from '@/components/common/Markdown';
import { useAssistantChat } from './queries';
import type { AssistantMessage } from '@/lib/types';
import { cn } from '@/lib/utils';

const QUICK_ACTIONS = [
  { label: 'Buscar procedimiento', prompt: 'Necesito el procedimiento para ' },
  { label: 'Resumen del artículo', prompt: 'Resúmeme el artículo actual: ' },
  { label: 'Artículos relacionados', prompt: 'Sugiéreme artículos relacionados con ' },
  { label: 'Contactos útiles', prompt: '¿Qué contactos internos tengo para ' },
];

export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const chat = useAssistantChat();

  const send = async (question: string) => {
    const q = question.trim();
    if (!q || chat.isPending) return;
    setInput('');
    setMessages((m) => [...m, { rol: 'user', contenidoMd: q }, { rol: 'assistant', contenidoMd: '', pendiente: true }]);

    try {
      const res = await chat.mutateAsync({ question: q, conversationId });
      setConversationId(res.conversationId);
      setMessages((m) => {
        const next = [...m];
        const lastIdx = next.length - 1;
        next[lastIdx] = {
          rol: 'assistant',
          contenidoMd: res.message,
          references: res.references,
        };
        return next;
      });
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          rol: 'assistant',
          contenidoMd: 'No pude obtener una respuesta. Inténtalo nuevamente.',
        };
        return next;
      });
    }
  };

  return (
    <>
      <Button
        variant="brand"
        size="icon"
        className="fixed bottom-6 right-6 h-12 w-12 rounded-full shadow-xl"
        onClick={() => setOpen((v) => !v)}
        title="Asistente IA"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </Button>

      <aside
        className={cn(
          'bg-card fixed right-0 top-0 z-50 flex h-screen w-full max-w-md flex-col border-l shadow-2xl transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="text-brand h-4 w-4" />
            <h3 className="font-semibold tracking-tight">Asistente</h3>
            <span className="text-muted-foreground text-xs">· Base de Conocimiento</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <ScrollArea className="flex-1">
          <div className="space-y-4 p-4">
            {messages.length === 0 && <Welcome onPick={(p) => setInput(p)} />}
            {messages.map((m, i) => (
              <MessageBubble key={i} message={m} />
            ))}
          </div>
        </ScrollArea>

        <footer className="border-t p-3">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={2}
              placeholder="Pregúntale al asistente sobre cualquier procedimiento o artículo…"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              className="flex-1 resize-none"
            />
            <Button onClick={() => send(input)} disabled={chat.isPending || !input.trim()}>
              {chat.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-muted-foreground mt-1.5 text-[10px]">
            Respuestas basadas en la documentación interna de Repuestos España. El asistente solo cita
            artículos que existen.
          </p>
        </footer>
      </aside>
    </>
  );
}

function Welcome({ onPick }: { onPick: (prompt: string) => void }) {
  return (
    <div className="space-y-4">
      <div className="bg-brand/5 border-brand/20 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="text-brand h-4 w-4" />
          <h4 className="text-sm font-semibold">Hola 👋</h4>
        </div>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
          Soy tu asistente interno. Puedo buscar procedimientos, resumir artículos y guiarte por la
          base de conocimiento. Pregúntame en lenguaje natural.
        </p>
      </div>

      <div>
        <h5 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
          Acciones rápidas
        </h5>
        <div className="grid grid-cols-2 gap-2">
          {QUICK_ACTIONS.map((q) => (
            <Button
              key={q.label}
              variant="outline"
              size="sm"
              className="h-auto justify-start whitespace-normal py-2 text-left text-xs"
              onClick={() => onPick(q.prompt)}
            >
              {q.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: AssistantMessage }) {
  if (message.rol === 'user') {
    return (
      <div className="ml-8 flex justify-end">
        <div className="bg-brand text-brand-foreground max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
          {message.contenidoMd}
        </div>
      </div>
    );
  }

  return (
    <div className="mr-8">
      {message.pendiente ? (
        <div className="bg-muted text-muted-foreground inline-flex items-center gap-2 rounded-2xl rounded-tl-sm px-3 py-2 text-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Pensando…
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-secondary rounded-2xl rounded-tl-sm px-3 py-2">
            <Markdown content={message.contenidoMd} className="text-sm" />
          </div>
          {message.references && message.references.length > 0 && (
            <div className="pl-2">
              <div className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                Referencias
              </div>
              <ul className="space-y-1">
                {message.references.map((r) => (
                  <li key={r.refId}>
                    <a
                      href={r.url}
                      className="text-brand hover:bg-brand/5 group inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs"
                    >
                      {r.kind === 'article' ? (
                        <FileText className="h-3 w-3" />
                      ) : (
                        <Hash className="h-3 w-3" />
                      )}
                      {r.titulo}
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
